import { ApiError, hasHistoryContext } from './api.js';

// ════════════════════════════════════════════════════════════════════
// Agents digiTRANS (P1, contrat §4 et §11.1) : routes des cartes « Nouvel agent » et
// « Modification d'agent », du centre des agents et de la fiche d'un agent.
// Toute écriture part d'un clic de l'utilisateur (D4) : l'assistant ne fait que préparer la
// carte, dont l'état est tenu par le serveur (table cartes_agents) ; le corps ne porte que
// l'identifiant de la carte et les champs modifiés, la mission est relue en base.
// Le demandeur est identifié par les en-têtes X-Entreprise-Id et X-User-Api-Hash, jamais
// dans l'URL (journaux nginx) ; réponses jamais mises en cache (no-store).
// Sur le modèle d'emailRequest (services/api.js), sans le modifier : la base des URL et les
// en-têtes de contexte, non exportés là-bas, sont recopiés ici.
// ════════════════════════════════════════════════════════════════════

// import.meta.env n'existe que sous Vite : le module reste importable par les tests node
const API_BASE_URL = (import.meta.env && import.meta.env.VITE_API_URL) || 'http://localhost:3001';

// Profil de l'utilisateur (X-Profil, X-Poste encodé : un en-tête HTTP reste en ASCII), gardé avec
// l'abonnement pour adapter les alertes à son poste
function contextHeaders(context) {
  return {
    'X-Entreprise-Id': String(context.entreprise_id),
    'X-User-Api-Hash': String(context.user_api_hash),
    ...(context.profil ? { 'X-Profil': String(context.profil) } : {}),
    ...(context.poste ? { 'X-Poste': encodeURIComponent(String(context.poste)) } : {}),
  };
}

const enc = (value) => encodeURIComponent(String(value));

/** Événement de window émis après chaque écriture réussie sur les agents. */
export const AGENTS_MODIFIES = 'digitrans:agents-modifies';

/**
 * Requête d'une route d'agents → données JSON de la réponse.
 * Sans contexte complet : ApiError 401 CONTEXT_REQUIRED, rien ne part. Connexion coupée
 * (fetch lève un TypeError) : ApiError de statut 0 et de code NETWORK (la demande a pu
 * arriver au serveur). Erreur HTTP : ApiError {status, code} ({error, code} du serveur), avec
 * `agentId` quand le serveur nomme un agent (409 AGENT_EN_DOUBLE : la veille identique à
 * rejoindre) et `carte` quand il joint l'état gardé de la carte.
 */
async function agentsRequest(path, context, { method = 'GET', body, signal } = {}) {
  if (!hasHistoryContext(context)) {
    throw new ApiError('Ouvrez l’assistant depuis la plateforme digiTRANS pour gérer vos agents.', { status: 401, code: 'CONTEXT_REQUIRED' });
  }
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api${path}`, {
      method,
      headers: { ...contextHeaders(context), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      cache: 'no-store',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ApiError('Connexion au serveur interrompue : l’issue de la demande est inconnue.', { status: 0, code: 'NETWORK' });
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new ApiError(data.error || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
    error.agentId = data.agent_id ?? null;
    error.carte = data.carte && typeof data.carte === 'object' ? data.carte : null;
    throw error;
  }
  // Écriture réussie (activation, pause, reprise, suppression, abonnement…) : la pastille du
  // bouton « Agents » se relit (useCompteAgents)
  if (method !== 'GET' && typeof window !== 'undefined') window.dispatchEvent(new Event(AGENTS_MODIFIES));
  return data;
}

// Champs que la carte « Nouvel agent » peut renvoyer à l'activation : rien d'autre ne part
const MODIFICATIONS = ['champs', 'canaux', 'telephone', 'destinataires_externes'];

// Forme courte acceptée par abonner : abonner(agentId, ctx) (contrat §11.1, sans canaux)
const estContexte = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value) && 'entreprise_id' in value);

export const agentsApi = {
  /**
   * GET /api/agents/cartes/:carte → {carte: {statut, agent_id, mission_appliquee?, essai_envoye?,
   * destinataires?: {proprietaire: {email_masque, telephone_masque}, externes}}}
   * statut : proposee | activee | rejointe | annulee | appliquee (carte de modification) ;
   * destinataires : masques des coordonnées appliquées (jamais en clair).
   */
  carte(carteId, ctx, { signal } = {}) {
    return agentsRequest(`/agents/cartes/${enc(carteId)}`, ctx, { signal });
  },

  /**
   * POST /api/agents → 201 {agent} ; même carte rejouée : 200 {agent, deja: true} ; doublon :
   * 409 AGENT_EN_DOUBLE (error.agentId). modifications : seulement ce que l'utilisateur a changé
   * dans la carte, {champs?, canaux?, telephone?, destinataires_externes?} (voir
   * components/agents/agentSpec.js, verifierChamps) ; toute autre clé est ignorée.
   * destinataires_externes, quand il est présent, est la liste complète voulue : une adresse
   * proposée gardée y figure par son rang dans spec.destinataires.externes (le serveur seul la
   * connaît en clair), une adresse ajoutée en clair.
   */
  activer(carteId, modifications, ctx) {
    const source = modifications && typeof modifications === 'object' ? modifications : {};
    const corps = { carte_id: carteId };
    for (const cle of MODIFICATIONS) if (source[cle] !== undefined) corps[cle] = source[cle];
    return agentsRequest('/agents', ctx, { method: 'POST', body: corps });
  },

  /**
   * POST /api/agents/cartes/:carte/essai → {envois: [{canal, statut}]} : message d'essai au
   * seul demandeur, marqué « essai ». canaux : canaux cochés dans la carte (facultatif ;
   * l'application et l'e-mail sont toujours ajoutés par le serveur) ; options.telephone : numéro
   * saisi dans la carte (E.164).
   */
  essayer(carteId, canaux, ctx, { telephone = null } = {}) {
    const corps = { ...(Array.isArray(canaux) ? { canaux } : {}), ...(telephone ? { telephone } : {}) };
    return agentsRequest(`/agents/cartes/${enc(carteId)}/essai`, ctx, { method: 'POST', body: corps });
  },

  /** POST /api/agents/cartes/:carte/annuler → {carte: {statut: "annulee"}} */
  annulerCarte(carteId, ctx) {
    return agentsRequest(`/agents/cartes/${enc(carteId)}/annuler`, ctx, { method: 'POST' });
  },

  /**
   * POST /api/agents/:id/abonnement {carte_id, canaux?, telephone?} : « Rejoindre la veille
   * existante » depuis la carte d'un doublon ; options.canaux : canaux cochés dans la carte ;
   * options.telephone : numéro saisi (E.164).
   */
  rejoindre(agentId, carteId, ctx, { canaux, telephone = null } = {}) {
    return agentsRequest(`/agents/${enc(agentId)}/abonnement`, ctx, {
      method: 'POST',
      body: { carte_id: carteId, ...(Array.isArray(canaux) ? { canaux } : {}), ...(telephone ? { telephone } : {}) },
    });
  },

  /**
   * PATCH /api/agents/:id {carte_id} : applique la carte « Modification d'agent » (quelle que
   * soit son action, lue en base par le serveur) ; 409 AGENT_MODIFIE si l'agent a changé de
   * version depuis la carte.
   */
  appliquer(agentId, carteId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}`, ctx, { method: 'PATCH', body: { carte_id: carteId } });
  },

  /** GET /api/agents → {mes_agents: [...], abonnements: [...], entreprise: null} */
  liste(ctx, { signal } = {}) {
    return agentsRequest('/agents', ctx, { signal });
  },

  /**
   * GET /api/agents/:id → {agent, role, version, abonnes, alertes_30j, alertes,
   * prochaine_execution, courbe: {seuil, points: [{date, valeur, declenche}]},
   * rapports: [{id, lien, pages, cree_le}], mon_abonnement}
   */
  fiche(agentId, ctx, { signal } = {}) {
    return agentsRequest(`/agents/${enc(agentId)}`, ctx, { signal });
  },

  /**
   * GET /api/agents/:id/executions?avant=&limite= → {executions: [{debut, verdict, appels,
   * duree_ms, erreur}]} ; opts : {avant?, limite?, signal?} (curseurs non sensibles).
   */
  executions(agentId, { avant = null, limite = null, signal } = {}, ctx) {
    const params = new URLSearchParams();
    if (avant != null && avant !== '') params.set('avant', String(avant));
    if (limite != null && limite !== '') params.set('limite', String(limite));
    // toString() plutôt que size : URLSearchParams.size manque à Safari avant la version 17,
    // où la pagination partirait sans ses paramètres
    const requete = params.toString();
    return agentsRequest(`/agents/${enc(agentId)}/executions${requete ? `?${requete}` : ''}`, ctx, { signal });
  },

  /** POST /api/agents/:id/pause → état de l'agent (propriétaire seulement) */
  pause(agentId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}/pause`, ctx, { method: 'POST' });
  },

  /** POST /api/agents/:id/reprise → état de l'agent */
  reprise(agentId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}/reprise`, ctx, { method: 'POST' });
  },

  /** POST /api/agents/:id/execution : « Exécuter maintenant » (tâche manuelle déposée) */
  executer(agentId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}/execution`, ctx, { method: 'POST' });
  },

  /** DELETE /api/agents/:id : agent archivé (après un ConfirmDialog de l'interface) */
  supprimer(agentId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}`, ctx, { method: 'DELETE' });
  },

  /**
   * POST /api/agents/:id/abonnement {canaux?} : s'abonner, ou changer ses canaux d'abonné.
   * abonner(agentId, canaux, ctx) ; forme courte abonner(agentId, ctx) sans canaux.
   */
  abonner(agentId, canaux, ctx) {
    const [liste, contexte] = ctx === undefined && estContexte(canaux) ? [null, canaux] : [canaux, ctx];
    return agentsRequest(`/agents/${enc(agentId)}/abonnement`, contexte, {
      method: 'POST',
      body: Array.isArray(liste) ? { canaux: liste } : {},
    });
  },

  /** DELETE /api/agents/:id/abonnement : se désabonner */
  desabonner(agentId, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}/abonnement`, ctx, { method: 'DELETE' });
  },

  /** POST /api/agents/:id/sourdine {jours: 1–30} : sourdine de l'abonné */
  sourdine(agentId, jours, ctx) {
    return agentsRequest(`/agents/${enc(agentId)}/sourdine`, ctx, { method: 'POST', body: { jours } });
  },
};
